import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Chip, CircularProgress, DialogTitle, IconButton, Tooltip, Typography } from '@mui/material';
import RefreshIcon  from '@mui/icons-material/Refresh';
import AssessmentIcon from '@mui/icons-material/Assessment';
import {
  fetchReportJobData, deleteReportJob, pollReportJobStatus,
  setSelectedReportJob, clearReportJobData,
} from '../reduxStore/ReportJobSlice';
import { AppDispatch } from '../reduxStore/Store';
import MyTable from '../components/MyTable';
import { ButtonType, StatutJob } from '../constants/enums';
import { TableHeader, TableAction } from '../constants/interfaces';
import { ReportJob } from '../models/models';
import ReportJobForm from '../components/Forms/ReportJobForm';
import { useDialog } from '../components/DialogComponent';
import { useSnackbar } from '../components/SnackbarProvider';
import { openReportFile } from '../utils/reportFile';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';

const STATUT_COLORS: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  PENDING:    'default',
  PROCESSING: 'warning',
  DONE:       'success',
  ERROR:      'error',
};

const STATUT_LABELS: Record<string, string> = {
  PENDING:    'En attente',
  PROCESSING: 'En cours...',
  DONE:       'Terminé',
  ERROR:      'Erreur',
};

const TYPE_LABELS: Record<string, string> = {
  DOCUMENT_VENTE:     'Documents de vente',
  LISTE_ARTICLE:      'Liste des articles',
  STOCK:              'État du stock',
  TIERS:              'Liste des tiers',
  COMPTE_CLIENT:      'Compte client',
  COMPTE_FOURNISSEUR: 'Compte fournisseur',
  COMPTE_CAISSE:      'État de caisse',
  LISTE_REGLEMENT:    'Liste des règlements',
};

const GestionReportJob: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { reportJobData, total, loading, error, successMessage } =
    useSelector((state: any) => state.ReportJobSlice);
  const selectedDepartement = useSelector((state: any) => state.AppSlice.selectedDepartement);

  const [fetchParams, setFetchParams] = useState<{ limit: number; offset: number; departement_id: number | null }>({
    limit: 10, offset: 0, departement_id: selectedDepartement?.id ?? null,
  });
  const [FormDialog, openDialog, closeDialog] = useDialog({ dialogProps: { maxWidth: 'sm', fullWidth: true } });
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    dispatch(fetchReportJobData(fetchParams));
    return () => {
      dispatch(setSelectedReportJob(null));
      dispatch(clearReportJobData());
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [dispatch, fetchParams, successMessage]);

  useEffect(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    const pendingJobs = reportJobData.filter(
      (j: ReportJob) => j.statut === StatutJob.PENDING || j.statut === StatutJob.PROCESSING
    );
    if (pendingJobs.length > 0) {
      pollingRef.current = setInterval(() => {
        pendingJobs.forEach((j: ReportJob) => dispatch(pollReportJobStatus(j.id!)));
      }, 4000);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [reportJobData, dispatch]);

  useEffect(() => {
    if (error)          showSnackbar(error, 'error');
    if (successMessage) showSnackbar(successMessage, 'success');
  }, [error, successMessage, dispatch, showSnackbar]);

  const handleDownload = async (job: ReportJob) => {
    try {
      // Téléchargement (attachment) — driver-agnostique (local/s3 via presigned URL)
      await openReportFile(job.id!, {
        mode:     'attachment',
        fileName: `rapport_${job.id}`,
        format:   job.format,
      });
    } catch {
      showSnackbar('Téléchargement échoué', 'error');
    }
  };

  const headers: TableHeader<ReportJob>[] = [
    { key: 'id',           label: '#' },
    { key: 'type_rapport', label: 'Type',     render: (row: any) => TYPE_LABELS[row.type_rapport] ?? row.type_rapport },
    { key: 'format',       label: 'Format' },
    { key: 'template_nom', label: 'Template', render: (row: any) => row.template_nom ?? '— défaut —' },
    {
      key: 'statut', label: 'Statut',
      render: (row: any) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={STATUT_LABELS[row.statut] ?? row.statut} color={STATUT_COLORS[row.statut] ?? 'default'} size="small" />
          {(row.statut === StatutJob.PENDING || row.statut === StatutJob.PROCESSING) && <CircularProgress size={14} />}
        </Box>
      ),
    },
    { key: 'created_at', label: 'Demandé le', render: (row: any) => row.created_at ? new Date(row.created_at).toLocaleString('fr-FR') : '' },
  ];

  const tableActions: TableAction<ReportJob>[] = [
    {
      label: 'Télécharger',
      type: ButtonType.DOWNLOAD,
      callback: handleDownload,
    },
    {
      label: 'Supprimer',
      type: ButtonType.DELETE,
      callback: (j) => dispatch(deleteReportJob(j.id!)),
    },
  ];

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title="Rapports générés"
        subtitle={`${total} rapport${total > 1 ? 's' : ''}`}
        icon={<AssessmentIcon />}
        addLabel="Générer un rapport"
        onAdd={openDialog}
        actions={
          <Tooltip title="Rafraîchir">
            <IconButton
              onClick={() => dispatch(fetchReportJobData(fetchParams))}
              sx={{
                color: '#344054',
                bgcolor: '#fff',
                border: '1px solid #d0d5dd',
                borderRadius: 2,
                '&:hover': { borderColor: '#98a2b3', bgcolor: '#f4f5f7' },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      {loading && <Typography color="#667085" mb={2}>Chargement...</Typography>}

      <AppSurface>
        <MyTable
          headers={headers}
          data={reportJobData}
          actions={tableActions}
          onPageChange={(page, rows) => setFetchParams(prev => ({ ...prev, limit: rows, offset: page * rows }))}
          totalRows={total}
        />
      </AppSurface>

      <FormDialog>
        <DialogTitle>Nouveau rapport</DialogTitle>
        <ReportJobForm onCancel={closeDialog} />
      </FormDialog>
    </AppPage>
  );
};

export default GestionReportJob;
